import {
  type ReactElement, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { bindCreateChatAiChatId } from './bindWatchAiChat';
import {
  DOSSIER_SEARCH_KINDS,
  dossierSearchKindOf,
  dossierTabSearchUserTurn,
  setDossierSearchKind,
} from './dossierSearchChat';
import { planInvestigationStepChat } from './investigationStepChat';
import {
  buildCompanyDossierContext,
} from './watchAiChat';
import WatchAiChatDialog from './WatchAiChatDialog';
import { dossierChatScreen } from './watchChatScreen';

import type { IInvestigationStepAttachment } from './investigationStepChat';
import type { WatchCompany, WatchDossierSearchKind, WatchTowerMockState } from './types';

import styles from './scss/TargetedMarketing.module.scss';

interface IWatchTowerDossierChatDialog {
  open: boolean,
  chatId: number,
  company: WatchCompany,
  state: WatchTowerMockState,
  onClose: () => void,
  onApply: (next: WatchTowerMockState) => void,
  onRerunInvestigation?: () => Promise<void>,
  stepAttachment?: IInvestigationStepAttachment | null,
  onClearStep?: () => void,
}

function WatchTowerDossierChatDialog({
  open,
  chatId,
  company,
  state,
  onClose,
  onApply,
  onRerunInvestigation,
  stepAttachment = null,
  onClearStep,
}: IWatchTowerDossierChatDialog): ReactElement {
  const { t } = useTranslation();
  const sendRef = useRef<((text: string) => Promise<number | null>) | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const chat = state.createChats.find((item) => item.id === chatId);
  const activeKind = dossierSearchKindOf(chat);
  const context = useMemo(
    () => buildCompanyDossierContext(state, company),
    [company, state],
  );
  const findingCount = Array.isArray(context.findings) ? context.findings.length : 0;
  const stepPlan = useMemo(
    () => (stepAttachment
      ? planInvestigationStepChat(company, context, stepAttachment)
      : null),
    [company, context, stepAttachment],
  );
  const screen = useMemo(
    () => dossierChatScreen(company, context, stepPlan?.customData),
    [company, context, stepPlan],
  );

  const onSearchKind = async (kind: WatchDossierSearchKind) => {
    if (!sendRef.current) {
      toast.error(t('targetedMarketing.chatBindFailed'));
      return;
    }
    setIsSearching(true);
    onApply(setDossierSearchKind(state, chatId, kind));
    try {
      await sendRef.current(dossierTabSearchUserTurn(kind));
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    } finally {
      setIsSearching(false);
    }
  };

  const onRerun = async () => {
    if (!onRerunInvestigation) {
      return;
    }
    setIsRerunning(true);
    try {
      await onRerunInvestigation();
      toast.success(t('targetedMarketing.investigationRetryQueued'));
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    } finally {
      setIsRerunning(false);
    }
  };

  const isBusy = isSearching || isRerunning;

  return (
    <WatchAiChatDialog
      open={open}
      title={chat?.title || t('targetedMarketing.talkToAi')}
      description={t('targetedMarketing.dossierChatHint')}
      nestChatId={chat?.aiChatId ?? null}
      screen={screen}
      sendRef={sendRef}
      onClose={onClose}
      onChatCreated={(aiChatId) => {
        onApply(bindCreateChatAiChatId(state, chatId, aiChatId));
        toast.success(t('targetedMarketing.chatBound'));
      }}
      extraSlot={null}
      contextSlot={(
        <>
          <p className={styles.muted}>
            {t('targetedMarketing.dossierChatContext', {
              company: company.name,
              findings: findingCount,
            })}
          </p>
          {stepAttachment && stepPlan ? (
            <div className={styles.runCard}>
              <p className={styles.muted}>
                {stepPlan.wire === 'inline' ? t('targetedMarketing.investigationStepAttachedInline', {
                  runId: stepAttachment.runId,
                  stepIndex: stepAttachment.stepIndex,
                  promptRole: stepAttachment.promptRole,
                  companyId: stepAttachment.watchCompanyId,
                }) : null}
                {stepPlan.wire === 'reference' ? t('targetedMarketing.investigationStepAttachedReference', {
                  count: stepPlan.responseChars,
                }) : null}
                {stepPlan.wire === 'over_cap' ? t('targetedMarketing.investigationStepAttachedOverCap', {
                  count: stepPlan.responseChars,
                }) : null}
              </p>
              {onClearStep ? (
                <button
                  type="button"
                  className={styles.nameButton}
                  onClick={onClearStep}
                >
                  {t('targetedMarketing.investigationStepRemove')}
                </button>
              ) : null}
            </div>
          ) : null}
          {activeKind ? (
            <p className={styles.muted}>
              {t('targetedMarketing.dossierSearchOn', {
                tab: t(`targetedMarketing.dossierSearch_${activeKind}`),
              })}
            </p>
          ) : null}
          <div className={styles.searchActionRow}>
            {DOSSIER_SEARCH_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                disabled={isBusy}
                className={
                  kind === activeKind
                    ? `${styles.choiceCard} ${styles.searchAction} ${styles.choiceCardActive}`
                    : `${styles.choiceCard} ${styles.searchAction}`
                }
                onClick={() => {
                  onSearchKind(kind).catch(() => undefined);
                }}
              >
                {t(`targetedMarketing.dossierSearch_${kind}`)}
              </button>
            ))}
          </div>
          <div className={styles.searchActionRow}>
            <button
              type="button"
              disabled={isBusy || !onRerunInvestigation}
              className={`${styles.choiceCard} ${styles.searchAction}`}
              onClick={() => {
                onRerun().catch(() => undefined);
              }}
            >
              {t('targetedMarketing.dossierRerunInvestigation')}
            </button>
          </div>
        </>
      )}
    />
  );
}

WatchTowerDossierChatDialog.defaultProps = {
  onRerunInvestigation: undefined,
  stepAttachment: null,
  onClearStep: undefined,
};

export default WatchTowerDossierChatDialog;
